import {
  PurchaseOrderDetailsInput,
  TicketGroupInput,
  TicketGroupIntention,
} from 'src/WebApiController';

import { MAX_TICKET_GROUPS_PER_BATCH } from './BatchedPurchaseOrderOperator.constants';

export const groupTicketGroupsByEvent = (
  ticketGroups: TicketGroupInput[]
): Map<string, TicketGroupInput[]> => {
  const grouped = new Map<string, TicketGroupInput[]>();
  for (const tg of ticketGroups) {
    const key = tg.viagogoVirtualId ?? '';
    const existing = grouped.get(key);
    if (existing) {
      existing.push(tg);
    } else {
      grouped.set(key, [tg]);
    }
  }
  return grouped;
};

/**
 * Batches event keys so each batch contains up to MAX_TICKET_GROUPS_PER_BATCH
 * ticket groups. Events are never split across batches — if adding the next
 * event would exceed the limit, a new batch is started. An event is always
 * added to the current batch if it's empty (even if it alone exceeds the limit).
 */
export const batchEventKeys = (
  eventKeys: string[],
  groupedByEvent: Map<string, TicketGroupInput[]>
): string[][] => {
  const batches: string[][] = [];
  let currentBatch: string[] = [];
  let currentCount = 0;

  for (const key of eventKeys) {
    const eventTGCount = groupedByEvent.get(key)!.length;

    if (
      currentBatch.length > 0 &&
      currentCount + eventTGCount > MAX_TICKET_GROUPS_PER_BATCH
    ) {
      batches.push(currentBatch);
      currentBatch = [];
      currentCount = 0;
    }

    currentBatch.push(key);
    currentCount += eventTGCount;
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

export const buildInput = (
  base: PurchaseOrderDetailsInput,
  ticketGroups: TicketGroupInput[],
  purchaseOrderId?: number
): PurchaseOrderDetailsInput => {
  return {
    ...base,
    id: purchaseOrderId ?? base.id,
    ticketGroups,
  };
};

/**
 * Filters intentions to only include those whose ticketGroupId is present
 * in the given ticket groups. Used to derive the intentions subset for a
 * batch that was partitioned by ticket groups — ticket groups without an
 * intention (e.g. newly created TGs) are included in the batch but simply
 * produce no intention entries.
 */
export const filterIntentionsForTicketGroups = (
  intentions: TicketGroupIntention[],
  ticketGroups: TicketGroupInput[]
): TicketGroupIntention[] => {
  const tgIds = new Set(ticketGroups.map((tg) => tg.ticketGroupId));
  return intentions.filter((i) => tgIds.has(i.ticketGroupId));
};
